/*
 * @Author: Chao Yang
 * @Date: 2020-01-10 10:11:11
 * @LastEditor: Chao Yang
 * @LastEditTime : 2020-01-10 09:59:18
 */
import { Subject } from 'rxjs';

export class PdfPrintService {
  currentPage = -1;
  disableCreateObjectURL: any;
  pageStyleSheet: HTMLStyleElement;
  scratchCanvas: HTMLCanvasElement;
  running = false;
  onProgress = new Subject<{ index: number; total: number }>();

  constructor(
    private pdfDocument: any,
    private pagesOverview: any,
    private printContainer: HTMLDivElement
  ) {
    const disableCreateObjectURL = 'disableCreateObjectURL';
    this.disableCreateObjectURL =
      pdfDocument.loadingParams[disableCreateObjectURL];
  }

  destroy(): void {
    this.currentPage = -1;
    this.running = false;
    this.printContainer.textContent = '';
    window.removeEventListener('onafterprint', this.destroy.bind(this));
    const body = document.querySelector('body');
    body.removeAttribute('data-pdfjsprinting');
    if (this.pageStyleSheet) {
      this.pageStyleSheet.remove();
      this.pageStyleSheet = null;
    }
    this.scratchCanvas.width = this.scratchCanvas.height = 0;
    this.scratchCanvas = null;
  }

  layout(): void {
    this.scratchCanvas = document.createElement('canvas');
    const body = document.querySelector('body');
    body.setAttribute('data-pdfjsprinting', '');
    this.pageStyleSheet = document.createElement('style');
    const pageSize = this.pagesOverview[0];
    this.pageStyleSheet.textContent =
      '@supports ((size:A4) and (size:1pt 1pt)) {' +
      '@page { size: ' +
      pageSize.width +
      'pt ' +
      pageSize.height +
      'pt;}' +
      '}';
    body.appendChild(this.pageStyleSheet);
  }

  performPrint() {
    return new Promise(resolve => {
      setTimeout(() => {
        window.print();
        setTimeout(resolve, 20);
      }, 0);
    });
  }

  print() {
    if (this.running) {
      return;
    }
    this.running = true;
    window.addEventListener('onafterprint', this.destroy.bind(this));
    this.layout();
    this.renderPages()
      .then(() => {
        return this.performPrint();
      })
      .catch(() => {})
      .then(() => {
        this.destroy();
      });
  }

  renderPage(pageNumber, size) {
    const CSS_UNITS = 96.0 / 72.0;
    const PRINT_RESOLUTION = 150;
    const PRINT_UNITS = PRINT_RESOLUTION / 72.0;
    const scratchCanvas = this.scratchCanvas;
    scratchCanvas.width = Math.floor(size.width * PRINT_UNITS);
    scratchCanvas.height = Math.floor(size.height * PRINT_UNITS);
    const width = Math.floor(size.width * CSS_UNITS) + 'px';
    const height = Math.floor(size.height * CSS_UNITS) + 'px';
    const ctx = scratchCanvas.getContext('2d');
    ctx.save();
    ctx.fillStyle = 'rgb(255, 255, 255)';
    ctx.fillRect(0, 0, scratchCanvas.width, scratchCanvas.height);
    ctx.restore();
    return this.pdfDocument
      .getPage(pageNumber)
      .then(pdfPage => {
        const renderContext = {
          canvasContext: ctx,
          transform: [PRINT_UNITS, 0, 0, PRINT_UNITS, 0, 0],
          viewport: pdfPage.getViewport({ scale: 1, rotation: size.rotation }),
          intent: 'print'
        };
        return pdfPage.render(renderContext).promise;
      })
      .then(() => {
        return {
          width,
          height
        };
      });
  }

  renderPages() {
    const pageCount = this.pagesOverview.length;
    const renderNextPage = (resolve, reject) => {
      if (++this.currentPage >= pageCount) {
        this.renderProgress(pageCount, pageCount);
        resolve();
        return;
      }
      const index = this.currentPage;
      this.renderProgress(index, pageCount);
      this.renderPage(index + 1, this.pagesOverview[index])
        .then(this.useRenderedPage.bind(this))
        .then(() => {
          renderNextPage(resolve, reject);
        }, reject);
    };
    return new Promise(renderNextPage);
  }

  renderProgress(index: number, total: number): void {
    this.onProgress.next({
      index,
      total
    });
  }

  useRenderedPage(printItem) {
    const img = document.createElement('img');
    img.style.width = printItem.width;
    img.style.height = printItem.height;
    const scratchCanvas = this.scratchCanvas;
    if ('toBlob' in scratchCanvas && !this.disableCreateObjectURL) {
      scratchCanvas.toBlob(blob => {
        img.src = URL.createObjectURL(blob);
      });
    } else {
      img.src = scratchCanvas.toDataURL();
    }
    const wrapper = document.createElement('div');
    wrapper.appendChild(img);
    this.printContainer.appendChild(wrapper);
    return new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });
  }
}
